import { checkpointFieldsOf, checkpointState, nextOpenCheckpoint } from '@ofmos/shared/checklist';
import { Fragment, useRef } from 'react';
import type { FieldMetadata, FieldValue, ObjectMetadata } from '../../../data/client';
import { useRecord } from '../../../data/hooks';
import { FieldDisplay } from '../../../fields/display/FieldDisplay';
import { FieldProvider } from '../../../fields/FieldContext';
import { Badge, cx, Icon, Skeleton } from '../../../ui';
import { MOTION, useMotion } from '../../../ui/motion';
import { TransitionLink } from '../../nav/RouteTransition';
import { deadlineSentence, toCheckpointField } from '../../record/checklist';
import { todayKey } from '../../record/dueDate';
import { ArchivedRowAction } from './ArchivedRowAction';
import { RecordRowMenu } from './RecordRowMenu';
import { displayContext, recordTitle, type TableColumn } from './tableModel';

/**
 * ONE RECORD AS A PHONE LIST ROW (P4-6; spec/screens/list.md section 3 item 3, and section
 * 7's last item: "on the phone Apple does not draw a table: a row is an avatar, a bold title
 * line, a secondary line, a grey preview and a trailing time with a chevron").
 *
 * WHAT THIS WAS: a card printing up to three LABEL-AND-VALUE pairs in a wrapped definition
 * list under the title - "STAGE Active   MRR EUR 2,400   OWNER Lena Roth". That is a table
 * row folded onto a phone: it reads the column headings out loud on every row, and no list
 * in iOS does it. It is the kit's Large row now: the identifier as the title, the highlight
 * values as ONE secondary line, the last of them as the trailing value, and the disclosure
 * chevron at the trailing edge.
 *
 * The title is the link and its `::after` stretches over the whole row, so the tap target is
 * the row while the accessible name stays the record's title - no nested anchors, and
 * nothing that only works on hover.
 *
 * Editing is deliberately NOT here. On a phone a 40px cell holding a popover is a mis-tap
 * waiting to happen; the row opens the record page, which has room for real inputs.
 */
export interface RecordCardProps {
  readonly object: ObjectMetadata;
  /** The fields this VIEW shows, for the touch-and-hold menu's stage (F2). See `RecordTableRow`. */
  readonly fields: readonly FieldMetadata[];
  /** The view's board field: what "stage" means on this object, for the row menu. */
  readonly boardGroupFieldId: string | null;
  /** The fields shown under the title, already trimmed to what a phone can hold. */
  readonly secondary: readonly TableColumn[];
  /**
   * EVERY visible column, not only the three the card prints (`phase-b-coach.md` D5). The
   * checklist line is computed over the row's checkpoint fields, and those are typically the
   * columns the card had to drop: three of eleven is what fits under a title, and the dot row
   * is what says something useful about the other eight.
   */
  readonly columns: readonly TableColumn[];
  readonly recordId: string;
  readonly present?: boolean;
  readonly onExited?: () => void;
  /** D21's Archived segment: the card states it and carries the restore. */
  readonly archived?: boolean | undefined;
}

export function RecordCard({
  object,
  fields,
  boardGroupFieldId,
  secondary,
  columns,
  recordId,
  present = true,
  onExited,
  archived = false,
}: RecordCardProps) {
  const record = useRecord(object.id, recordId);
  const retained = useRef(record.data);
  if (record.data != null) retained.current = record.data;
  const row = present ? record.data : retained.current;
  const motion = useMotion(present, { onExited });

  if (!motion.mounted) return null;

  if (row === undefined || row === null) {
    return <PlaceholderCard fieldCount={secondary.length} />;
  }

  const title = recordTitle(row, object.labelIdentifierFieldId);

  /**
   * THE LAST HIGHLIGHT VALUE GOES TO THE TRAILING SLOT, the rest onto the secondary line -
   * which is Mail's shape (sender, subject, preview, then the time on the right) applied to
   * a record: the value a reader scans down the trailing edge is the one that sorts them,
   * and on this product's lists that is the last column the view keeps.
   */
  const trailingColumn = secondary.at(-1);
  const subtitleColumns = secondary.slice(0, -1);

  return (
    /**
     * TOUCH AND HOLD OPENS THE ROW'S MENU (spec section 3 item 4; rulebook PH5, A7). It is
     * the same menu the Mac opens on a secondary click - `ContextMenu` carries the gesture
     * for both pointers - so the phone and the desktop cannot drift into two answers.
     *
     * THE SWIPE ACTION IS NOT BUILT IN THIS ROUND and is named in the slice's report: the
     * product has no row-swipe gesture to reuse (`useSwipeDismiss` dismisses a SHEET), and
     * the spec itself records that no Apple reference image for it exists.
     */
    <RecordRowMenu
      object={object}
      fields={fields}
      targetIds={[recordId]}
      archived={archived}
      boardGroupFieldId={boardGroupFieldId}
      onOpen={() => undefined}
    >
      <li
        ref={motion.ref}
        data-state={motion.state}
        inert={!present ? true : undefined}
        className={cx(
          MOTION.row,
          'list-phone-row relative',
          /**
           * THE PRESS SITS ON THE `<li>`, not on the link, because the tap target is the whole
           * row: the title's stretched `::after` is what a thumb hits, and `:active` matches an
           * activated element's ANCESTORS, so the row lights up from a press anywhere on it.
           *
           * An ARCHIVED row has no overlay (the Restore button lives inside it and an overlay
           * would eat its taps), so there is nothing to press and it does not pretend otherwise.
           */
          !archived && 'pressable',
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <TransitionLink
              direction="push"
              to={`/objects/${object.id}/records/${recordId}`}
              className={cx(
                'min-w-0 flex-1 truncate text-base font-semibold',
                // Dimmed via the muted INK token, never an opacity wash: see RecordTableRow.
                archived
                  ? 'text-ink-muted'
                  : // The stretched overlay makes the whole row one tap target - but only when
                    // there is no second control on it. On an archived row the Restore button
                    // sits inside this <li>, and an overlay would eat every tap meant for it.
                    "text-ink after:absolute after:inset-0 after:content-['']",
              )}
            >
              {title}
            </TransitionLink>
            {archived && (
              <Badge tone="neutral" size="sm">
                Archived
              </Badge>
            )}
          </div>
          {/*
          THE CHECKLIST, AS A PHONE READS IT (`phase-b-coach.md` D5). A row has space for one
          line about eight dates, and the coach's question is not "which eight" - it is "how
          far along, and what is next".
        */}
          <CardChecklistLine columns={columns} values={row.values} />
          {subtitleColumns.length > 0 && (
            /*
            ONE SECONDARY LINE, VALUES ONLY. The labels are gone with the definition list:
            a phone row that names its columns on every row is a table wearing a list's
            clothes, and the reader already knows what the view is showing.

            ONE GREY LINE, ONE BASELINE (P4 round 2, finding F5). Round 1 nested the middle
            dot INSIDE the value's own span, in front of a display that renders a block, so
            the separator was pushed onto a line of its own above a name in the link blue,
            beside a tinted capsule - three registers in one line of a Mail row that has one.
            The separator is a sibling of the values now, and the two flags are what make
            every value plain text of the same size: `tagAsDot` draws a select as text with
            its colour dot (the record's flag too since P5 round 3), and `listRow` says a
            relation, an email, a URL or a phone number is not an anchor, because the ROW is
            the link (iPhone Mail: the secondary line is one grey line of text).
          */
            <p className="list-phone-secondary">
              {subtitleColumns.map((column, index) => (
                <Fragment key={column.field.id}>
                  {index > 0 && (
                    <span className="shrink-0" aria-hidden="true">
                      ·
                    </span>
                  )}
                  <span className="min-w-0 truncate">
                    <FieldProvider
                      value={{
                        ...displayContext(object.id, row, column.field),
                        tagAsDot: true,
                        listRow: true,
                      }}
                    >
                      <FieldDisplay />
                    </FieldProvider>
                  </span>
                </Fragment>
              ))}
            </p>
          )}
        </div>
        {archived ? (
          <ArchivedRowAction objectId={object.id} recordId={recordId} title={title} />
        ) : (
          <span className="list-phone-trailing">
            {trailingColumn !== undefined && (
              <span className="min-w-0 truncate">
                {/* The trailing value is the same plain text the secondary line is (F5): it
                    sits at the row's trailing edge in front of the chevron, and a capsule or
                    a blue name there would compete with the disclosure. */}
                <FieldProvider
                  value={{
                    ...displayContext(object.id, row, trailingColumn.field),
                    tagAsDot: true,
                    listRow: true,
                  }}
                >
                  <FieldDisplay />
                </FieldProvider>
              </span>
            )}
            <Icon name="chevron-right" className="text-ink-subtle" />
          </span>
        )}
      </li>
    </RecordRowMenu>
  );
}

/**
 * THE DOT ROW AND ONE SENTENCE (`phase-b-coach.md` D5).
 *
 * `nextOpenCheckpoint` picks by DEADLINE rather than by declaration order, which is what
 * makes this line useful rather than decorative: the next thing to do is the one that is due
 * soonest, and on a row that has fallen behind that is the oldest overdue one.
 *
 * THE WARNING COLOUR IS ON THE LATE HALF ONLY. "Next: 48-hour check-in" is a fact and
 * "3 days late" is the alarm; painting the whole line warning-coloured would spend a semantic
 * colour on the label, which DESIGN.md reserves for what actually means something.
 */
function CardChecklistLine({
  columns,
  values,
}: {
  readonly columns: readonly TableColumn[];
  readonly values: Readonly<Record<string, FieldValue>>;
}) {
  const today = todayKey();
  const fields = checkpointFieldsOf(columns.map((column) => toCheckpointField(column.field)));
  if (fields.length === 0) return null;

  const next = nextOpenCheckpoint(values, fields, today);
  const tail = next === null ? null : deadlineSentence(next.state);

  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="flex items-center gap-1" aria-hidden="true">
        {fields.map((field) => {
          const status = checkpointState(field, values, today).status;
          return (
            <span
              key={field.id}
              className={cx(
                'size-1.5 rounded-full',
                status === 'done'
                  ? 'bg-ink-muted'
                  : status === 'late'
                    ? 'border border-warning'
                    : 'border border-line-strong',
              )}
            />
          );
        })}
      </span>
      {next !== null && (
        <span className="min-w-0 truncate text-2xs text-ink-muted">
          {`Next: ${next.field.label}`}
          {tail !== null && (
            <>
              {' · '}
              <span className={next.state.status === 'late' ? 'text-warning' : undefined}>
                {tail}
              </span>
            </>
          )}
        </span>
      )}
    </div>
  );
}

/** Card-shaped loading placeholder: same height, same rhythm, nothing jumps. */
export function PlaceholderCard({ fieldCount }: { readonly fieldCount: number }) {
  return (
    <li className="list-phone-row flex-col justify-center gap-2">
      <Skeleton className="h-4 w-40" />
      {fieldCount > 0 && <Skeleton className="h-3 w-56" />}
    </li>
  );
}
